export type ReviewHomeKind = 'pipeline' | 'matters';

export type ReviewHomeSnapshot =
  | {
      kind: 'pipeline';
      values: Record<string, unknown>;
      mainScrollTop: number;
      mainScrollLeft: number;
      boardScrollLeft: number;
      stageScrollTops?: Record<number, number>;
    }
  | {
      kind: 'matters';
      values: Record<string, unknown>;
      mainScrollTop: number;
      mainScrollLeft: number;
    };

export type ReviewHomeTab = {
  key: 'home';
  kind: ReviewHomeKind;
  label: string;
  url: string;
  snapshot: ReviewHomeSnapshot;
};

export type MatterTab = {
  key: `matter:${number}`;
  matterId: number;
  title: string;
  url: string;
};

export type MatterTabKey = MatterTab['key'];

export type MatterTabsState = {
  home: ReviewHomeTab | null;
  matters: MatterTab[];
  activeKey: 'home' | MatterTabKey | null;
};

export const initialMatterTabsState: MatterTabsState = {
  home: null,
  matters: [],
  activeKey: null,
};

export type MatterTabsAction =
  | { type: 'REGISTER_HOME'; home: ReviewHomeTab }
  | { type: 'OPEN_MATTER_BACKGROUND'; matterId: number; title: string; url: string }
  | { type: 'ACTIVATE'; key: 'home' | MatterTabKey | null }
  | { type: 'UPDATE_MATTER_URL'; matterId: number; url: string }
  | { type: 'CLOSE_MATTER'; matterId: number }
  | { type: 'CLOSE_ALL' };

export function isMatterTabUrl(rawUrl: string, expectedMatterId?: number): boolean {
  if (!rawUrl.startsWith('/') || rawUrl.startsWith('//')) return false;
  const parsed = new URL(rawUrl, 'https://valentine.invalid');
  if (parsed.origin !== 'https://valentine.invalid') return false;
  const match = parsed.pathname.match(/^\/matters\/(\d+)(?:\/.*)?$/);
  if (!match) return false;
  return expectedMatterId === undefined || Number(match[1]) === expectedMatterId;
}

function matterKey(matterId: number): MatterTabKey {
  return `matter:${matterId}`;
}

export function matterTabsReducer(state: MatterTabsState, action: MatterTabsAction): MatterTabsState {
  switch (action.type) {
    case 'REGISTER_HOME': {
      if (state.matters.length > 0 && state.home?.kind !== action.home.kind) return state;
      if (
        state.home &&
        state.home.kind === action.home.kind &&
        state.home.label === action.home.label &&
        state.home.url === action.home.url
      ) {
        state.home.snapshot = action.home.snapshot;
        return state;
      }
      return {
        ...state,
        home: action.home,
        activeKey: state.activeKey ?? 'home',
      };
    }
    case 'OPEN_MATTER_BACKGROUND': {
      if (!isMatterTabUrl(action.url, action.matterId)) return state;
      const nextTab: MatterTab = {
        key: matterKey(action.matterId),
        matterId: action.matterId,
        title: action.title.trim() || `Matter ${action.matterId}`,
        url: action.url,
      };
      const existingIndex = state.matters.findIndex((tab) => tab.matterId === action.matterId);
      if (existingIndex === -1) return { ...state, matters: [...state.matters, nextTab] };
      const matters = [...state.matters];
      matters[existingIndex] = nextTab;
      return { ...state, matters };
    }
    case 'ACTIVATE':
      return { ...state, activeKey: action.key };
    case 'UPDATE_MATTER_URL': {
      if (!isMatterTabUrl(action.url, action.matterId)) return state;
      return {
        ...state,
        matters: state.matters.map((tab) => (
          tab.matterId === action.matterId ? { ...tab, url: action.url } : tab
        )),
      };
    }
    case 'CLOSE_MATTER': {
      const index = state.matters.findIndex((tab) => tab.matterId === action.matterId);
      if (index === -1) return state;
      const matters = state.matters.filter((tab) => tab.matterId !== action.matterId);
      if (state.activeKey !== matterKey(action.matterId)) return { ...state, matters };
      const nextActive = matters[index] ?? matters[index - 1];
      return { ...state, matters, activeKey: nextActive?.key ?? (state.home ? 'home' : null) };
    }
    case 'CLOSE_ALL':
      return { ...state, matters: [], activeKey: state.home ? 'home' : null };
  }
}
